import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import logging from "../utils/logging";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Brain, FileText, Loader2, Clock, ShieldAlert, Flame, HeartPulse, AlertTriangle, Sparkles, ChevronRight,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeIcons = { security: ShieldAlert, fire: Flame, medical: HeartPulse, accident: AlertTriangle };
const typeColors = { security: "text-red-500", fire: "text-orange-500", medical: "text-blue-500", accident: "text-yellow-600" };

function formatTime(isoStr) {
  return new Date(isoStr).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const mdComponents = {
  h1: ({ children }) => <h1 className="font-heading text-2xl font-black tracking-tight mb-3 mt-4 text-gray-900">{children}</h1>,
  h2: ({ children }) => <h2 className="font-heading text-lg font-bold tracking-tight mb-2 mt-5 text-gray-900 border-b border-gray-200 pb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="font-heading text-base font-semibold mb-1 mt-3 text-gray-800">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-gray-700 font-body mb-2 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-gray-700 font-body">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  blockquote: ({ children }) => <blockquote className="border-l-3 border-gray-300 pl-3 italic text-gray-600 my-2">{children}</blockquote>,
};

function MarkdownReport({ content }) {
  return <ReactMarkdown components={mdComponents}>{content}</ReactMarkdown>;
}

function LoadingState({ message, sub }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
      <p className="text-sm text-gray-500 font-body">{message}</p>
      {sub && <p className="text-xs text-gray-400 font-body mt-1">{sub}</p>}
    </div>
  );
}

function EmptyState({ icon: Icon, message }) {
  return (
    <div className="text-center py-16 text-gray-400">
      <Icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
      <p className="text-sm font-body">{message}</p>
    </div>
  );
}

function ReportBadge({ label, meta }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Badge className="bg-black text-white hover:bg-black text-[10px] rounded-sm">{label}</Badge>
      <span className="text-[10px] text-gray-400 font-body">{meta}</span>
    </div>
  );
}

// --- Tab Components ---

function SummaryTab({ onGenerate, report, loading }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-bold text-gray-900">{t("intel.summary_heading")}</h2>
          <p className="text-xs text-gray-500 font-body mt-0.5">
            {t("intel.summary_desc")}
          </p>
        </div>
        <Button onClick={onGenerate} disabled={loading} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-sm" data-testid="generate-summary-btn">
          {loading
            ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("intel.analyzing")}</>
            : <><Sparkles className="w-4 h-4 mr-2" /> {t("intel.generate")}</>
          }
        </Button>
      </div>
      {loading && <LoadingState message={t("intel.analyzing_long")} sub={t("intel.may_take")} />}
      {report && !loading && (
        <div className="border border-gray-200 rounded-sm p-5 bg-gray-50/50" data-testid="summary-report-content">
          <ReportBadge label={t("intel.ai_report")} meta={`${t("intel.alerts_analyzed", { n: report.alert_count })} \u00B7 ${formatTime(report.created_at)}`} />
          <MarkdownReport content={report.content} />
        </div>
      )}
      {!report && !loading && <EmptyState icon={Brain} message={t("intel.click_to_generate")} />}
    </div>
  );
}

function AlertAnalysisTab({ alerts, onAnalyze, report, loading, selectedId, setSelectedId }) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
        <div className="flex-1">
          <h2 className="font-heading text-lg font-bold text-gray-900 mb-1">{t("intel.alert_heading")}</h2>
          <p className="text-xs text-gray-500 font-body mb-3">{t("intel.alert_desc")}</p>
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="rounded-sm border-gray-300 font-body text-sm" data-testid="select-alert-trigger">
              <SelectValue placeholder={t("intel.select_alert_placeholder")} />
            </SelectTrigger>
            <SelectContent className="rounded-sm max-h-64">
              {alerts.map((alert) => {
                const Icon = typeIcons[alert.alert_type] || ShieldAlert;
                return (
                  <SelectItem key={alert.alert_id} value={alert.alert_id} className="font-body text-sm">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-3.5 h-3.5 ${typeColors[alert.alert_type]}`} />
                      <span className="truncate max-w-[280px]">{alert.description}</span>
                      <Badge className="text-[9px] px-1 py-0 ml-1 bg-gray-100 text-gray-600 hover:bg-gray-100 rounded-sm">{alert.alert_type}</Badge>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={onAnalyze} disabled={loading || !selectedId} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body text-sm" data-testid="analyze-alert-btn">
          {loading
            ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t("intel.analyzing")}</>
            : <><Brain className="w-4 h-4 mr-2" /> {t("intel.analyze")}</>
          }
        </Button>
      </div>
      {loading && <LoadingState message={t("intel.generating_threat")} sub={t("intel.may_take")} />}
      {report && !loading && (
        <div className="border border-gray-200 rounded-sm p-5 bg-gray-50/50" data-testid="alert-report-content">
          <ReportBadge label={t("intel.ai_analysis")} meta={`Alert: ${report.alert_id} \u00B7 ${formatTime(report.created_at)}`} />
          <MarkdownReport content={report.content} />
        </div>
      )}
      {!report && !loading && <EmptyState icon={FileText} message={t("intel.select_an_alert")} />}
    </div>
  );
}

function HistoryTab({ reports, viewingReport, setViewingReport }) {
  const { t } = useTranslation();
  if (reports.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm">
        <div className="px-5 py-3 border-b border-gray-200">
          <h2 className="font-heading text-lg font-bold text-gray-900">{t("intel.history_heading")}</h2>
          <p className="text-xs text-gray-500 font-body">{t("intel.history_desc")}</p>
        </div>
        <EmptyState icon={Clock} message={t("intel.no_reports")} />
      </div>
    );
  }

  const toggleReport = (report) => {
    setViewingReport(viewingReport?.report_id === report.report_id ? null : report);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm">
      <div className="px-5 py-3 border-b border-gray-200">
        <h2 className="font-heading text-lg font-bold text-gray-900">{t("intel.history_heading")}</h2>
        <p className="text-xs text-gray-500 font-body">{t("intel.history_desc")}</p>
      </div>
      <div className="divide-y divide-gray-100">
        {reports.map((report) => {
          const isExpanded = viewingReport?.report_id === report.report_id;
          const IconComp = report.type === "summary" ? Sparkles : FileText;
          const label = report.type === "summary" ? t("intel.summary_report_label") : t("intel.alert_analysis_label");
          return (
            <button
              key={report.report_id}
              onClick={() => toggleReport(report)}
              className="w-full text-left px-5 py-3 hover:bg-gray-50 transition-colors duration-100 flex items-center justify-between"
              data-testid={`history-item-${report.report_id}`}
            >
              <div className="flex items-center gap-3">
                <IconComp className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-800 font-body">
                    {label}
                    {report.alert_id && <span className="text-gray-400 ml-1">({report.alert_id})</span>}
                  </p>
                  <p className="text-[10px] text-gray-400 font-body">{formatTime(report.created_at)}</p>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`} />
            </button>
          );
        })}
      </div>
      {viewingReport && (
        <div className="border-t border-gray-200 p-5 bg-gray-50/50" data-testid="history-report-content">
          <ScrollArea className="max-h-[60vh]">
            <MarkdownReport content={viewingReport.content} />
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// --- Main Page ---

export default function IntelligencePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [selectedAlertId, setSelectedAlertId] = useState("");
  const [summaryReport, setSummaryReport] = useState(null);
  const [alertReport, setAlertReport] = useState(null);
  const [reportHistory, setReportHistory] = useState([]);
  const [viewingHistoryReport, setViewingHistoryReport] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingAlert, setLoadingAlert] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(res.data);
    } catch (err) {
      logging.error("Failed to fetch alerts for intelligence", err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch (err) {
      logging.error("Failed to fetch notifications", err);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reports/history`, { withCredentials: true });
      setReportHistory(res.data);
    } catch (err) {
      logging.error("Failed to fetch report history", err);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    fetchNotifications();
    fetchHistory();
  }, [fetchAlerts, fetchNotifications, fetchHistory]);

  const handleGenerateSummary = async () => {
    setLoadingSummary(true);
    setSummaryReport(null);
    try {
      const res = await axios.post(`${API}/reports/summary`, {}, { withCredentials: true });
      setSummaryReport(res.data);
      fetchHistory();
      toast.success(t("intel.summary_success"));
    } catch (err) {
      logging.error("Summary generation failed", err);
      toast.error(t("intel.summary_failed"));
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleAnalyzeAlert = async () => {
    if (!selectedAlertId) {
      toast.error(t("intel.select_alert_first"));
      return;
    }
    setLoadingAlert(true);
    setAlertReport(null);
    try {
      const res = await axios.post(`${API}/reports/alert/${selectedAlertId}`, {}, { withCredentials: true });
      setAlertReport(res.data);
      fetchHistory();
      toast.success(t("intel.analysis_success"));
    } catch (err) {
      logging.error("Alert analysis failed", err);
      toast.error(t("intel.analysis_failed"));
    } finally {
      setLoadingAlert(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F4F6]" data-testid="intelligence-page">
      <Header notifications={notifications} onNotificationRead={fetchNotifications} />

      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Brain className="w-7 h-7 text-gray-900" strokeWidth={2} />
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-gray-900">{t("intel.title")}</h1>
            <p className="text-sm text-gray-500 font-body">{t("intel.subtitle")}</p>
          </div>
        </div>

        <Tabs defaultValue="summary" className="space-y-4">
          <TabsList className="bg-white border border-gray-200 rounded-sm p-0.5">
            <TabsTrigger value="summary" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-summary">
              <Sparkles className="w-4 h-4 mr-1.5" /> {t("intel.tab_summary")}
            </TabsTrigger>
            <TabsTrigger value="alert" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-alert-analysis">
              <FileText className="w-4 h-4 mr-1.5" /> {t("intel.tab_alert")}
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-sm text-sm font-body data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-history">
              <Clock className="w-4 h-4 mr-1.5" /> {t("intel.tab_history")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="summary" className="space-y-4">
            <SummaryTab onGenerate={handleGenerateSummary} report={summaryReport} loading={loadingSummary} />
          </TabsContent>

          <TabsContent value="alert" className="space-y-4">
            <AlertAnalysisTab
              alerts={alerts} onAnalyze={handleAnalyzeAlert} report={alertReport}
              loading={loadingAlert} selectedId={selectedAlertId} setSelectedId={setSelectedAlertId}
            />
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <HistoryTab reports={reportHistory} viewingReport={viewingHistoryReport} setViewingReport={setViewingHistoryReport} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
